import api from "../config/api";
import { getErrorMessage } from "../config/errorHelper";

// ─────────────────────────────────────────────
//  NOTE : pas besoin d'ajouter le token manuellement ici.
//  api.js l'injecte automatiquement dans chaque requête
//  via un interceptor Axios (voir config/api.js).
// ─────────────────────────────────────────────

// ─────────────────────────────────────────────
//  LECTURE — séparées par scope
// ─────────────────────────────────────────────

/**
 * Credentials PERSONNELLES de l'utilisateur connecté.
 * Il peut voir, modifier et supprimer ces credentials.
 * Nécessite la permission : CREDENTIAL_VIEW_PERSONAL
 */
// ── Admin — liste par entreprise ─────────────────────────────────────
export const getCredentials = async (companyId, filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.keyword)  params.append("keyword",  filters.keyword);
    if (filters.scope)    params.append("scope",    filters.scope);
    if (filters.type)     params.append("type",     filters.type);
    if (filters.folderId) params.append("folderId", filters.folderId);
    const query = params.toString() ? `?${params}` : "";
    const res = await api.get(`/credentials/admin/company/${companyId}${query}`);
    return res.data;
  } catch (err) { throw new Error(getErrorMessage(err)); }
};

export const getPersonalCredentials = async () => {
  try {
    const res = await api.get("/credentials/personal");
    return res.data;
  } catch (err) {
    throw new Error(getErrorMessage(err));
  }
};

/**
 * Credentials D'ENTREPRISE partagées (lecture seule pour les clients).
 * L'utilisateur peut voir et copier le mot de passe, rien d'autre.
 * Nécessite la permission : CREDENTIAL_VIEW_COMPANY
 */
export const getCompanyCredentials = async () => {
  try {
    const res = await api.get("/credentials/company");
    return res.data;
  } catch (err) {
    throw new Error(getErrorMessage(err));
  }
};

// ─────────────────────────────────────────────
//  RECHERCHE ET FILTRES
// ─────────────────────────────────────────────

/** Recherche par nom dans toutes les credentials de l'entreprise. */
export const searchCredentials = async (keyword) => {
  try {
    const res = await api.get(`/credentials/search?keyword=${keyword}`);
    return res.data;
  } catch (err) {
    throw new Error(getErrorMessage(err));
  }
};

/** Filtre par type (ex: "SERVER", "SSH", "WEBSITE"…). */


// ─────────────────────────────────────────────
//  DÉTAIL ET MOT DE PASSE
// ─────────────────────────────────────────────

/** Détail d'une credential par ID. */
export const getCredentialById = async (id) => {
  try {
    const res = await api.get(`/credentials/${id}`);
    return res.data;
  } catch (err) {
    throw new Error(getErrorMessage(err));
  }
};

/**
 * Récupère le mot de passe déchiffré d'une credential.
 * Le déchiffrement se fait côté backend — on ne stocke jamais le résultat.
 *
 * PERSONAL → nécessite CREDENTIAL_VIEW_PERSONAL
 * COMPANY  → nécessite CREDENTIAL_COPY_COMPANY
 */
export const getCredentialPassword = async (id) => {
  try {
    const res = await api.get(`/credentials/${id}/password`);
    return res.data;
  } catch (err) {
    throw new Error(getErrorMessage(err));
  }
};

/**
 * Historique des anciens mots de passe d'une credential PERSONNELLE
 * (même route/permissions que getCredentialPassword).
 * @returns {Array<{ password: string, replacedAt: string }>} du plus récent au plus ancien
 */
export const getCredentialPasswordHistory = async (id) => {
  try {
    const res = await api.get(`/credentials/${id}/password-history`);
    return res.data?.data ?? res.data;
  } catch (err) {
    throw new Error(getErrorMessage(err));
  }
};

/**
 * Journalise la consultation/copie d'un secret (mot de passe, champ
 * personnalisé masqué, ou code TOTP) — credential PERSONNELLE.
 * À appeler en "fire-and-forget" : un échec ne doit jamais bloquer l'action
 * réelle (copie, révélation) qui vient d'avoir lieu côté utilisateur.
 * @param {"PASSWORD"|"CUSTOM_FIELD"|"TOTP"} targetType
 * @param {"VIEW"|"COPY"} action
 * @param {string} [fieldName] nom du champ personnalisé, si targetType = CUSTOM_FIELD
 */
export const logCredentialAccess = async (id, { targetType, action, fieldName } = {}) => {
  try {
    const res = await api.post(`/credentials/${id}/access-log`, { targetType, action, fieldName });
    return res.data;
  } catch (err) {
    throw new Error(getErrorMessage(err));
  }
};

/**
 * Journal d'accès d'une credential PERSONNELLE (qui a consulté/copié quoi, et quand).
 * @returns page Spring Data : { content: [...], totalElements, totalPages, ... }
 */
export const getCredentialAccessLog = async (id, page = 0, size = 20) => {
  try {
    const res = await api.get(`/credentials/${id}/access-log?page=${page}&size=${size}`);
    return res.data?.data ?? res.data;
  } catch (err) {
    throw new Error(getErrorMessage(err));
  }
};

// ─────────────────────────────────────────────
//  CRÉATION
//  Nécessite la permission : CREDENTIAL_EDIT_PERSONAL
// ─────────────────────────────────────────────

/**
 * Crée une credential.
 * payload : { name, username?, encryptedPassword, url?, host?, port?, notes?, type, scope }
 */
export const createCredential = async (payload) => {
  try {
    const res = await api.post("/credentials/create", payload);
    if (res.data?.code === "ERROR") throw new Error(res.data.message);
    return res.data;
  } catch (err) {
    if (!(err.response || err.request)) throw err; // re-throw our own errors
    throw new Error(getErrorMessage(err));
  }
};

/**
 * Crée une credential — variante ADMIN, pour le compte d'une entreprise cliente.
 * Contrairement à createCredential (personnelle, liée à l'admin connecté),
 * celle-ci respecte l'entreprise ciblée dans le payload (company.id) et
 * force le scope COMPANY côté backend. Réservée aux pages admin
 * (nécessite le rôle SUPER_ADMIN).
 */
export const adminCreateCredential = async (payload) => {
  try {
    const res = await api.post("/credentials/admin/create", payload);
    if (res.data?.code === "ERROR") throw new Error(res.data.message);
    return res.data;
  } catch (err) {
    if (!(err.response || err.request)) throw err;
    throw new Error(getErrorMessage(err));
  }
};

// ─────────────────────────────────────────────
//  MODIFICATION
//  Nécessite la permission : CREDENTIAL_EDIT_PERSONAL
//  Le backend rejette toute tentative sur une COMPANY credential.
// ─────────────────────────────────────────────

export const updateCredential = async (id, payload) => {
  try {
    const res = await api.put(`/credentials/update/${id}`, payload);
    if (res.data?.code === "ERROR") throw new Error(res.data.message);
    return res.data;
  } catch (err) {
    if (!(err.response || err.request)) throw err;
    throw new Error(getErrorMessage(err));
  }
};

/**
 * Modifie une credential — variante ADMIN (tous scopes, toutes entreprises).
 * Réservée aux pages admin (nécessite le rôle SUPER_ADMIN).
 */
export const adminUpdateCredential = async (id, payload) => {
  try {
    const res = await api.put(`/credentials/admin/update/${id}`, payload);
    if (res.data?.code === "ERROR") throw new Error(res.data.message);
    return res.data;
  } catch (err) {
    if (!(err.response || err.request)) throw err;
    throw new Error(getErrorMessage(err));
  }
};

// ─────────────────────────────────────────────
//  SUPPRESSION
//  Nécessite la permission : CREDENTIAL_EDIT_PERSONAL
//  Le backend rejette toute tentative sur une COMPANY credential.
// ─────────────────────────────────────────────

export const deleteCredential = async (id) => {
  try {
    const res = await api.delete(`/credentials/${id}`);
    return res.data;
  } catch (err) {
    throw new Error(getErrorMessage(err));
  }
};

/**
 * Supprime une credential — variante ADMIN, sans restriction de scope.
 * Réservée aux pages admin (nécessite le rôle SUPER_ADMIN).
 */
export const adminDeleteCredential = async (id) => {
  try {
    const res = await api.delete(`/credentials/admin/delete/${id}`);
    return res.data;
  } catch (err) {
    throw new Error(getErrorMessage(err));
  }
};

/**
 * Génère un mot de passe fort conforme à la politique de l'entreprise.
 * Le backend applique les règles configurées par l'admin (longueur, majuscules, etc.)
 * @param {number} length longueur souhaitée (défaut : 16)
 */
export const generatePassword = async (length = 16) => {
  try {
    const res = await api.get(`/credentials/generate-password?length=${length}`);
    // Le backend retourne { code: "SUCCESS", data: "leMotDePasse" }
    return res.data?.data ?? res.data;
  } catch (err) {
    throw new Error(getErrorMessage(err));
  }
};

/**
 * Génère un mot de passe fort — variante ADMIN, pour une entreprise donnée.
 * Utilisée depuis les pages admin (ex: PasswordsPage) où l'admin agit
 * pour le compte d'une entreprise cliente précise.
 * @param {string} companyId UUID de l'entreprise ciblée
 * @param {number} length    longueur souhaitée (défaut : 16)
 */
export const adminGeneratePassword = async (companyId, length = 16) => {
  try {
    const res = await api.get(`/credentials/admin/generate-password/${companyId}?length=${length}`);
    // Le backend retourne { code: "SUCCESS", data: "leMotDePasse" }
    return res.data?.data ?? res.data;
  } catch (err) {
    throw new Error(getErrorMessage(err));
  }
};


// ─────────────────────────────────────────────
//  TOTP — Authentification à deux facteurs
// ─────────────────────────────────────────────

/**
 * Enregistre le secret TOTP d'une credential.
 * L'utilisateur copie le secret depuis l'app tierce (GitHub, Google…).
 * @param {string} id       UUID de la credential
 * @param {string} secret   Secret TOTP en clair (ex: "JBSWY3DPEHPK3PXP")
 */
export const saveTotpSecret = async (id, secret) => {
  try {
    const res = await api.put(`/credentials/${id}/totp`, { secret });
    if (res.data?.code === "ERROR") throw new Error(res.data.message);
    return res.data;
  } catch (err) {
    if (!(err.response || err.request)) throw err;
    throw new Error(getErrorMessage(err));
  }
};

/** Supprime le secret TOTP d'une credential (désactive le 2FA). */
export const deleteTotpSecret = async (id) => {
  try {
    const res = await api.delete(`/credentials/${id}/totp`);
    if (res.data?.code === "ERROR") throw new Error(res.data.message);
    return res.data;
  } catch (err) {
    if (!(err.response || err.request)) throw err;
    throw new Error(getErrorMessage(err));
  }
};

/**
 * Récupère le code TOTP actuel pour une credential.
 * @returns {{ code: string, secondsRemaining: number, period: number }}
 */
export const getTotpCode = async (id) => {
  try {
    const res = await api.get(`/credentials/${id}/totp`);
    return res.data?.data ?? res.data;
  } catch (err) {
    throw new Error(getErrorMessage(err));
  }
};

// ─────────────────────────────────────────────
//  TOTP — variantes ADMIN
//  À utiliser depuis les pages admin (companyId différent de celui de
//  l'admin connecté) : les endpoints personnels ci-dessus échouent sinon
//  avec "Access denied" (checkCompanyAccess compare à l'entreprise de
//  l'admin, pas à celle du client). Nécessite le rôle SUPER_ADMIN.
// ─────────────────────────────────────────────

export const adminSaveTotpSecret = async (id, secret) => {
  try {
    const res = await api.put(`/credentials/admin/${id}/totp`, { secret });
    if (res.data?.code === "ERROR") throw new Error(res.data.message);
    return res.data;
  } catch (err) {
    if (!(err.response || err.request)) throw err;
    throw new Error(getErrorMessage(err));
  }
};

export const adminDeleteTotpSecret = async (id) => {
  try {
    const res = await api.delete(`/credentials/admin/${id}/totp`);
    if (res.data?.code === "ERROR") throw new Error(res.data.message);
    return res.data;
  } catch (err) {
    if (!(err.response || err.request)) throw err;
    throw new Error(getErrorMessage(err));
  }
};

export const adminGetTotpCode = async (id) => {
  try {
    const res = await api.get(`/credentials/admin/${id}/totp`);
    return res.data?.data ?? res.data;
  } catch (err) {
    throw new Error(getErrorMessage(err));
  }
};

// ─────────────────────────────────────────────
//  DOSSIERS
// ─────────────────────────────────────────────

export const getFolders = async (companyId) => {
  try {
    const res = await api.get(`/credentials/admin/folders/${companyId}`);
    return res.data;
  } catch (err) { throw new Error(getErrorMessage(err)); }
};

export const createFolder = async (payload) => {
  try {
    const res = await api.post("/credentials/admin/folders", payload);
    if (res.data?.code === "ERROR") throw new Error(res.data.message);
    return res.data;
  } catch (err) {
    if (!(err.response || err.request)) throw err;
    throw new Error(getErrorMessage(err));
  }
};

export const updateFolder = async (id, payload) => {
  try {
    const res = await api.put(`/credentials/admin/folders/${id}`, payload);
    if (res.data?.code === "ERROR") throw new Error(res.data.message);
    return res.data;
  } catch (err) {
    if (!(err.response || err.request)) throw err;
    throw new Error(getErrorMessage(err));
  }
};

export const deleteFolder = async (id) => {
  try {
    const res = await api.delete(`/credentials/admin/folders/${id}`);
    return res.data;
  } catch (err) { throw new Error(getErrorMessage(err)); }
};

// ─────────────────────────────────────────────
//  POLITIQUE DE MOT DE PASSE
// ─────────────────────────────────────────────

export const getPolicy = async (companyId) => {
  try {
    const res = await api.get(`/credentials/admin/policy/${companyId}`);
    return res.data;
  } catch (err) { throw new Error(getErrorMessage(err)); }
};

export const savePolicy = async (companyId, policy) => {
  try {
    const res = await api.put(`/credentials/admin/policy/${companyId}`, policy);
    if (res.data?.code === "ERROR") throw new Error(res.data.message);
    return res.data;
  } catch (err) {
    if (!(err.response || err.request)) throw err;
    throw new Error(getErrorMessage(err));
  }
};


// ─────────────────────────────────────────────
//  FONCTIONS ADMIN SUPPLÉMENTAIRES
// ─────────────────────────────────────────────



export const getPassword = async (id) => {
  try {
    const res = await api.get(`/credentials/admin/${id}/password`);
    return res.data;
  } catch (err) { throw new Error(getErrorMessage(err)); }
};

/**
 * Historique des anciens mots de passe — variante ADMIN (sans restriction
 * de scope, sauf PERSONAL qui reste réservé à son propriétaire côté backend).
 * @returns {Array<{ password: string, replacedAt: string }>} du plus récent au plus ancien
 */
export const getPasswordHistory = async (id) => {
  try {
    const res = await api.get(`/credentials/admin/${id}/password-history`);
    return res.data?.data ?? res.data;
  } catch (err) { throw new Error(getErrorMessage(err)); }
};

/**
 * Journalise la consultation/copie d'un secret — variante ADMIN (sans
 * restriction de scope). Même usage "fire-and-forget" que logCredentialAccess.
 */
export const adminLogCredentialAccess = async (id, { targetType, action, fieldName } = {}) => {
  try {
    const res = await api.post(`/credentials/admin/${id}/access-log`, { targetType, action, fieldName });
    return res.data;
  } catch (err) { throw new Error(getErrorMessage(err)); }
};

/** Journal d'accès d'une credential précise — variante ADMIN. */
export const adminGetAccessLog = async (id, page = 0, size = 20) => {
  try {
    const res = await api.get(`/credentials/admin/${id}/access-log?page=${page}&size=${size}`);
    return res.data?.data ?? res.data;
  } catch (err) { throw new Error(getErrorMessage(err)); }
};

/**
 * Journal d'accès global d'une entreprise, toutes credentials confondues —
 * alimente la page dédiée « Journal d'accès » côté admin. Filtres tous
 * optionnels : action, targetType, dateFrom/dateTo ("yyyy-MM-dd"), search.
 */
// companyId est optionnel depuis le 15/09/2026 (à la demande du client :
// "je veux que par defaut sa affiche tout les log ... sans filtre") — omis,
// le journal couvre toutes les entreprises confondues.
export const adminGetAccessLogGlobal = async (companyId, {
  page = 0,
  size = 20,
  action,
  targetType,
  dateFrom,
  dateTo,
  search,
} = {}) => {
  try {
    const params = new URLSearchParams({ page, size });
    if (companyId)  params.set("companyId", companyId);
    if (action)     params.set("action", action);
    if (targetType) params.set("targetType", targetType);
    if (dateFrom)   params.set("dateFrom", dateFrom);
    if (dateTo)     params.set("dateTo", dateTo);
    if (search)     params.set("search", search);
    const res = await api.get(`/credentials/admin/access-log?${params.toString()}`);
    return res.data?.data ?? res.data;
  } catch (err) { throw new Error(getErrorMessage(err)); }
};

// ─────────────────────────────────────────────
//  COFFRE DE SECOURS HORS-LIGNE
// ─────────────────────────────────────────────

/** État actuel de la génération automatique — ne renvoie jamais la phrase secrète. */
export const adminGetVaultSettings = async (companyId) => {
  try {
    const res = await api.get(`/credentials/admin/vault-settings/${companyId}`);
    return res.data?.data ?? res.data;
  } catch (err) { throw new Error(getErrorMessage(err)); }
};

/**
 * Active/désactive la génération automatique nocturne.
 * passphrase est optionnel si on ne fait que réactiver (une phrase déjà
 * enregistrée est alors conservée côté backend).
 */
export const adminSaveVaultSettings = async (companyId, { vaultAutoExportEnabled, passphrase } = {}) => {
  try {
    const res = await api.put(`/credentials/admin/vault-settings/${companyId}`, {
      vaultAutoExportEnabled,
      passphrase,
    });
    if (res.data?.code === "ERROR") throw new Error(res.data.message);
    return res.data;
  } catch (err) {
    if (!(err.response || err.request)) throw err;
    throw new Error(getErrorMessage(err));
  }
};

/**
 * Génère le coffre de secours pour téléchargement manuel — la phrase secrète
 * est saisie à la volée et n'est JAMAIS stockée.
 *
 * Couvre à la fois l'export d'une seule entreprise (companyIds: [id]) et
 * l'export "toutes entreprises confondues" ou d'une sélection (companyIds
 * absent/vide = toutes), réunies dans le MÊME fichier — ainsi que le choix
 * du format : `.dcv` (lecteur autonome vault-reader.html) ou `.xlsx`
 * protégé par mot de passe (s'ouvre directement dans Excel).
 *
 * @param {{ companyIds?: string[], passphrase: string, format?: "DCV"|"XLSX" }} options
 * @returns {{ format: "DCV"|"XLSX", data: object }}
 *   DCV  → data est l'enveloppe JSON à écrire en .dcv
 *   XLSX → data est { filename, contentBase64 } (classeur chiffré encodé)
 *   Dans les deux cas, passer le résultat tel quel à downloadVaultFile().
 */
export const adminExportVault = async ({ companyIds, passphrase, format = "DCV" } = {}) => {
  try {
    const res = await api.post(`/credentials/admin/vault-export`, { companyIds, passphrase, format });
    if (res.data?.code === "ERROR") throw new Error(res.data.message);
    return { format, data: res.data?.data ?? res.data };
  } catch (err) {
    if (!(err.response || err.request)) throw err;
    throw new Error(getErrorMessage(err));
  }
};

/**
 * Déclenche le téléchargement du fichier renvoyé par adminExportVault, quel
 * que soit le format — centralise la construction du Blob pour ne pas la
 * dupliquer dans chaque écran qui propose l'export (fiche entreprise, export
 * multi-entreprises...).
 */
export const downloadVaultFile = ({ format, data }, suggestedName) => {
  let blob, filename;

  if (format === "XLSX") {
    const binary = atob(data.contentBase64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    blob = new Blob([bytes], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    filename = suggestedName || data.filename || "coffre-de-secours.xlsx";
  } else {
    blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/octet-stream" });
    filename = suggestedName || "coffre-de-secours.dcv";
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

// ─────────────────────────────────────────────
//  PARTAGE SÉCURISÉ À USAGE UNIQUE
// ─────────────────────────────────────────────

/**
 * Crée un lien de partage à usage unique pour une credential.
 * @param {string} id UUID de la credential
 * @param {{ durationHours?: 1|24|72, includeNotes?: boolean }} options
 * @returns {{ token: string, shareUrl: string, expiresAt: string }}
 */
export const adminCreateShare = async (id, { durationHours = 24, includeNotes = false } = {}) => {
  try {
    const res = await api.post(`/credentials/admin/${id}/share`, { durationHours, includeNotes });
    if (res.data?.code === "ERROR") throw new Error(res.data.message);
    return res.data?.data ?? res.data;
  } catch (err) {
    if (!(err.response || err.request)) throw err;
    throw new Error(getErrorMessage(err));
  }
};

/**
 * Historique des liens de partage d'une credential (du plus récent au plus
 * ancien), avec un statut calculé côté backend : ACTIVE, VIEWED, EXPIRED, REVOKED.
 */
export const adminGetShares = async (id) => {
  try {
    const res = await api.get(`/credentials/admin/${id}/shares`);
    return res.data?.data ?? res.data;
  } catch (err) { throw new Error(getErrorMessage(err)); }
};

/**
 * Révoque un lien de partage avant son expiration naturelle — fonctionne
 * aussi bien pour un partage individuel que pour un partage groupé du
 * coffre (même endpoint, le backend distingue les deux en interne).
 */
export const adminRevokeShare = async (token) => {
  try {
    const res = await api.delete(`/credentials/share/${token}`);
    if (res.data?.code === "ERROR") throw new Error(res.data.message);
    return res.data;
  } catch (err) {
    if (!(err.response || err.request)) throw err;
    throw new Error(getErrorMessage(err));
  }
};

// ─────────────────────────────────────────────
//  PARTAGE GROUPÉ DU COFFRE (une entreprise à la fois)
//  Même principe que le partage individuel (lien à usage unique,
//  expiration), avec en plus une phrase secrète optionnelle vu que
//  l'enjeu porte sur tout le coffre plutôt qu'un seul secret.
// ─────────────────────────────────────────────

/**
 * Crée un lien de partage pour TOUT le coffre d'une entreprise.
 * @param {{ companyId: string, durationHours?: 1|24|72, includeNotes?: boolean, passphrase?: string }} options
 * @returns {{ token: string, shareUrl: string, expiresAt: string, requiresPassphrase: boolean }}
 */
export const adminCreateVaultShare = async ({ companyId, durationHours = 24, includeNotes = false, passphrase } = {}) => {
  try {
    const res = await api.post(`/credentials/admin/vault-share`, { companyId, durationHours, includeNotes, passphrase });
    if (res.data?.code === "ERROR") throw new Error(res.data.message);
    return res.data?.data ?? res.data;
  } catch (err) {
    if (!(err.response || err.request)) throw err;
    throw new Error(getErrorMessage(err));
  }
};

/** Historique des partages groupés (coffre entier) d'une entreprise. */
export const adminGetVaultShares = async (companyId) => {
  try {
    const res = await api.get(`/credentials/admin/company/${companyId}/vault-shares`);
    return res.data?.data ?? res.data;
  } catch (err) { throw new Error(getErrorMessage(err)); }
};